import api, { Base, Tags } from 'ev-api/api';
import { sanitizeAndTransformResponse } from 'ev-api/common/transformers';

import {
  CreateSubmissionParams,
  GetHistoriesParams,
  GetSubmissionParams,
  GetVisitSubmissionsParams,
  PatchSubmissionParams,
} from './params';
import { GetCustomFormSubmissions, GetSubmission } from './responses';
import {
  transformSubmissionResponse,
  transformSubmissionsResponse,
} from './transformers';

// # As a patient
// # Index of submissions for patient's visits on current practice, patient_id must be current_user id
// #   GET /api/v4/patients/:patient_id/custom_form_submissions
// # Index of visit submissions, current_user must be the visit's patient
// #   GET /api/v4/visits/:visit_id/custom_form_submissions

export const submissionsApi = api.injectEndpoints({
  endpoints: builder => ({
    getVisitSubmissions: builder.query<
      GetCustomFormSubmissions,
      GetVisitSubmissionsParams
    >({
      query: ({ visitId, formTrigger, practiceId }) => ({
        url: `${Base.V4}/visits/${visitId}/custom_form_submissions`,
        method: 'GET',
        params: {
          include_available: true,
          event_trigger: formTrigger,
          sort_direction: 'ASC',
          sort_by: 'custom_form.title',
          practice_id: practiceId,
        },
      }),
      transformResponse: transformSubmissionsResponse,
      keepUnusedDataFor: 0,
      providesTags: [Tags.CustomFormSubmissions],
    }),
    getPatientSubmissionsHistory: builder.query<
      GetCustomFormSubmissions,
      GetHistoriesParams
    >({
      query: ({ patientId, formTrigger, currentVisitId }) => ({
        url: `${Base.V4}/patients/${patientId}/custom_form_submissions`,
        method: 'GET',
        params: {
          include_available: false,
          event_trigger: formTrigger,
          current_visit_id: currentVisitId ?? undefined,
        },
      }),
      transformResponse: transformSubmissionsResponse,
      providesTags: [Tags.CustomFormSubmissions],
    }),
    getSubmission: builder.query<GetSubmission, GetSubmissionParams>({
      query: ({ submissionId }) => ({
        url: `${Base.V4}/custom_form_submissions/${submissionId}`,
        method: 'GET',
      }),
      transformResponse: transformSubmissionResponse,
      providesTags: [Tags.CustomFormSubmissions],
    }),
    createCustomFormSubmission: builder.mutation<
      GetSubmission,
      CreateSubmissionParams
    >({
      query: ({
        customFormId,
        customFormVersionId,
        visitId,
        eventTrigger,
      }) => ({
        url: `${Base.V4}/visits/${visitId}/custom_form_submissions`,
        method: 'POST',
        body: {
          custom_form_submission: {
            custom_form_id: customFormId,
            custom_form_version_id: customFormVersionId,
            event_trigger: eventTrigger,
          },
        },
      }),
      transformResponse: sanitizeAndTransformResponse,
      invalidatesTags: [Tags.CustomFormSubmissions],
    }),
    createCustomFormSubmissionAndShareWithPatient: builder.mutation<
      void,
      CreateSubmissionParams
    >({
      query: ({
        customFormId,
        customFormVersionId,
        visitId,
        eventTrigger,
      }) => ({
        url: `${Base.V4}/visits/${visitId}/custom_form_submissions/share_with_patients`,
        method: 'POST',
        body: {
          custom_form_submission: {
            custom_form_id: customFormId,
            custom_form_version_id: customFormVersionId,
            event_trigger: eventTrigger,
          },
        },
      }),
      invalidatesTags: [Tags.CustomFormSubmissions, Tags.Visits],
    }),
    patchSubmission: builder.mutation<void, PatchSubmissionParams>({
      query: ({
        submissionId,
        customFormId,
        customFormVersionId,
        eDataValues,
      }) => ({
        url: `${Base.V4}/custom_form_submissions/${submissionId}`,
        method: 'PATCH',
        body: {
          custom_form_submission: {
            custom_form_id: customFormId,
            custom_form_version_id: customFormVersionId,
          },
          edata_pack: {
            edata_values_attributes: eDataValues,
          },
        },
      }),
      invalidatesTags: [Tags.CustomFormSubmissions, Tags.User],
    }),
    saveSubmission: builder.mutation<void, PatchSubmissionParams>({
      query: ({
        submissionId,
        customFormId,
        customFormVersionId,
        eDataValues,
      }) => ({
        url: `${Base.V4}/custom_form_submissions/${submissionId}/save`,
        method: 'PATCH',
        body: {
          custom_form_submission: {
            custom_form_id: customFormId,
            custom_form_version_id: customFormVersionId,
          },
          edata_pack: {
            edata_values_attributes: eDataValues,
          },
        },
      }),
      invalidatesTags: [Tags.CustomFormSubmissions, Tags.User],
    }),
    // Locks the form for editing to the current user
    lockSubmission: builder.mutation<GetSubmission, GetSubmissionParams>({
      query: ({ submissionId }) => ({
        url: `${Base.V4}/custom_form_submissions/${submissionId}/edit`,
        method: 'POST',
        keepUnusedDataFor: 0,
      }),
      transformResponse: sanitizeAndTransformResponse,
    }),
    // Unlocks the form so other users can request to edit
    unlockSubmission: builder.mutation<GetSubmission, GetSubmissionParams>({
      query: ({ submissionId }) => ({
        url: `${Base.V4}/custom_form_submissions/${submissionId}/cancel`,
        method: 'POST',
      }),
      transformResponse: sanitizeAndTransformResponse,
    }),
  }),
});

export const {
  useGetVisitSubmissionsQuery,
  useGetPatientSubmissionsHistoryQuery,
  useGetSubmissionQuery,
  useLazyGetSubmissionQuery,
  useCreateCustomFormSubmissionAndShareWithPatientMutation,
  useCreateCustomFormSubmissionMutation,
  usePatchSubmissionMutation,
  useSaveSubmissionMutation,
  useLockSubmissionMutation,
  useUnlockSubmissionMutation,
} = submissionsApi;
